<script setup lang="ts">
import { scrollTo, selectedScrollSnap } from "~/utils/carouselUtils";
import { twMerge } from "tailwind-merge";
import { ref, onMounted } from "vue";
import getAltFromProductImage from "~/utils/productAltGetter";

const TsCarouselV2 = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselV2.vue')
);
const TsProductVideoGallery = defineAsyncComponent(
    () => import('~/components/organisms/TsProductVideoGallery.vue')
);
const TsBadgeDiscountPercentage = defineAsyncComponent(
    () => import('~/components/atoms/TsBadgeDiscountPercentage.vue')
);
const TsCard = defineAsyncComponent(
    () => import('~/components/organisms/TsCard.vue')
);
const TsProductImageGallery = defineAsyncComponent(
    () => import('~/components/organisms/TsProductImageGallery.vue')
);

const TsCarouselSlide = defineAsyncComponent(
    () => import('~/components/organisms/TsCarouselSlide.vue')
);

const TsMediaContent = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaContent.vue')
);

const TsMediaEnd = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaEnd.vue')
);

type Props = {
  data?: object[];
  tags?: string[];
  product?: object;
  isSaved?: boolean;
  addedToSavedList?: boolean;
  dataTestId?: string;
};
const savelistStore = useSavelistStore();
const productStore = useProductStore();
const runtimeConfig = useRuntimeConfig();

// Function to check if the product is saved
const checkIfProductSaved = async () => {
  if (productStore.product.isSaved === undefined) {
    productStore.product.isSaved = await savelistStore.isThisProductSaved(
      productStore.product.code
    );
  }
};

// Run the check when the component is mounted or when the product code changes

const props = withDefaults(defineProps<Props>(), {
  data: () => [
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src:
        "https://www.youtube-nocookie.com/embed/q4vUP3AQ0cU?enablejsapi=1&amp;autoplay=0&amp;showinfo=0&amp;iv_load_policy=3&amp;controls=0",
      thumb: "https://img.youtube.com/vi/q4vUP3AQ0cU/default.jpg",
      type: "video",
      title: "The DEWALT DCD791D2 20V MAX XR Li-Ion Brushless Compact Drill Driver Kit.",
      sub_title: "Game-changer in the world of power tools.",
    },
    {
      src:
        "https://www.youtube-nocookie.com/embed/q4vUP3AQ0cU?enablejsapi=1&amp;autoplay=0&amp;showinfo=0&amp;iv_load_policy=3&amp;controls=0",
      thumb: "https://img.youtube.com/vi/q4vUP3AQ0cU/default.jpg",
      type: "video",
      title:
        "The DEWALT 2 DCD791D2 20V MAX XR Li-Ion Brushless Compact Drill Driver Kit.",
      sub_title: "Game-changer in the world of power tools.",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
    {
      src: "https://cdn.aws.toolstation.com/images/141020-UK/800/13036.jpg",
      thumb: "https://cdn.aws.toolstation.com/images/141020-UK/388/13036.jpg",
      type: "image",
    },
  ],
  tags: () => ["New", "20% off"],
  product: () => ({
    title:
      "Celebrate DeWalt's Century: 18V XR Brushless Combi Drill Kit - Limited Edition | Toolstation",
  }),
});

// Declare the missing reactive ref for modal
const product_images_and_videos_modal = ref(false);

const mainCarouselRef = ref(null);
const thumbCarouselRef = ref(null);
const mainCarouselApi = ref(null);
const thumbCarouselApi = ref(null);

const authStore = useAuthStore();
const { isMobile, isTablet, isDesktop } = useDevice();
const selected_index = ref(0);
const active_tab = ref("images");
const product_images = computed(() => {
  return props.data.filter((item) => item.type === "image");
});
const product_videos = computed(() => {
  return props.data.filter((item) => item.type === "video");
});
const modal_tabs = computed(() => {
  const tabs = [];
  if (product_images.value.length > 0) tabs.push("images");
  // if (product_videos.value.length > 0) tabs.push("videos");
  return tabs;
});

function openImageModal(index) {
  // Check if the item at the given index is an image in the props.data array
  const item = props.data[index];
  if (item.type === "image") {
    selected_index.value = index;  
  }
}

const selectedImageUrl = ref('')

const thumbCarouselOption = {
  containScroll: "keepSnaps",
  axis: "y",
  dragFree: true,
};

const onThumbClick = (index,item) => {
  if (!mainCarouselApi || !thumbCarouselApi) return;
  scrollTo(mainCarouselApi, index);
  logEvent({
        options: {
          eventAction: "click",
          eventProperties: {
           product_name:props?.product?.full_name || props?.product?.name,
           product_id : props?.product?.code,
           brand: props?.product?.brand || "",
           image_url:item?.src
          },
          eventInteraction: true,
          eventLabel: `Product detail page image slider changed to ${item?.src}`,
          eventName: "pdp_image_carousel",
        },
      });
  
};

const onSelect = () => {
  if (!mainCarouselApi || !thumbCarouselApi) return;
  selected_index.value = selectedScrollSnap(mainCarouselApi);
  scrollTo(thumbCarouselApi, selected_index.value);
};

onMounted(async () => {
  savelistStore.current_active_product = productStore.product;
  savelistStore.populateSavedList();
  await checkIfProductSaved();

  // Wait for the next DOM update cycle
  await nextTick();

  if (mainCarouselRef.value && thumbCarouselRef.value) {
    if (mainCarouselRef.value.emblaApi && thumbCarouselRef.value.emblaApi) {
      mainCarouselApi.value = mainCarouselRef.value.emblaApi;
      thumbCarouselApi.value = thumbCarouselRef.value.emblaApi;
      onSelect();
      mainCarouselApi.value?.on("reInit", onSelect).on("select", onSelect);
    } 
  } 
});

</script>

<template>
  <div
    class="product-thumbnail lg:max-h-[520px] lg:max-w-[640px] flex flex-col-reverse lg:flex-row gap-2"
  >
    <div class="min-w-[112px] shrink-0">
      <TsCarouselV2
        v-if="props.data.length > 1 || !isMobile"
        :options="thumbCarouselOption"
        ref="thumbCarouselRef"
        :layout="isMobile || isTablet ? 'horizontal' : 'vertical'"
        append-class="px-0"
        container-class="lg:gap-2 lg:max-h-[520px]"
        prev-button-class="lg:top-1"
        next-button-class="lg:-bottom-2"
        :navigation="!(isMobile || isTablet)"
        :pagination="!(isMobile || isTablet)"
      >
        <TsCarouselSlide
          v-for="(item, index) in props.data"
          :key="item"
          @click="openImageModal(index)"
          append-class="flex-[0_0_auto] w-[112px] h-[80px] lg:p-0"
        >
          <button
            :data-testid="`carousel-button-${props.dataTestId}`"
            @click="() => onThumbClick(index,item)"
            type="button"
            :class="
              twMerge(
                'flex justify-center w-full h-full bg-white rounded border border-grey p-2 overflow-hidden',
                item.type === 'video' && 'p-0',
                selected_index === index && 'border-info'
              )
            "
          >
            <TsImage
              v-if="item.type === 'image'"
              :src="item.thumb"
              width="64"
              height="64"
              :alt="getAltFromProductImage(item.thumb)"
              loading="lazy"
            />
            <div v-if="item.type === 'video' && !runtimeConfig.public.featureFlags.enableVideoly" class="relative m-auto before:z-10 before:content-[''] before:bg-idle-black/40 before:w-full before:h-full before:left-0 before:absolute before:top-0 ">
              <svg
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                width="37"
                height="37"
                viewBox="0 0 37 37"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.5007 33.9166C27.0153 33.9166 33.9173 27.0145 33.9173 18.4999C33.9173 9.98529 27.0153 3.08325 18.5007 3.08325C9.98603 3.08325 3.08398 9.98529 3.08398 18.4999C3.08398 27.0145 9.98603 33.9166 18.5007 33.9166Z"
                  stroke="white"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
                <path
                  d="M15.418 18.5V13.1597L20.043 15.8298L24.668 18.5L20.043 21.1702L15.418 23.8403V18.5Z"
                  stroke="white"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
              <TsImage
                :src="item.thumb"
                :data-testid="`thumb-${props.dataTestId}`"
                width="112"
                height="80"
                :alt="getAltFromProductImage(item.thumb)"
                loading="lazy"
              />   
            </div>
            <div  v-if="item.type === 'video' && runtimeConfig.public.featureFlags.enableVideoly " >
                <div id="videoly-videobox-placeholder" data-type="small"></div>
            </div>
          </button>
        </TsCarouselSlide>
      </TsCarouselV2>
    </div>
    <div
      class="grow relative bg-white lg:border lg:border-grey lg:rounded-lg md:min-w-[520px]"
    >
      <TsMedia append-class="lg:p-4">
        <TsMediaContent append-class="flex items-center">
          <template v-if="props.tags.length">
            <TsTag
              v-for="(item, index) in props.tags"
              :key="item"
              :data-testid="'pdp-product-deal-tag-' + index"
              :label="item.value"
              append-class="mr-1"
              :size="isDesktop ? 'sm' : 'xs'"
              rounded
              :variant=item.color
            />
          </template>
        </TsMediaContent>

        <TsMediaEnd>
          <TsButton
            append-class="hover:bg-natural-light-grey p-1"
            :data-testid="`${props.dataTestId}-save-list`"
            variant="secondary"
            @click="
              authStore.is_authenticated
                ? (savelistStore.modal_savelist_from_visible = true)
                : navigateTo('/login')
            "
            rounded
          >
          <TsIcon size="24" :name="productStore.product.isSaved ? 'solar:heart-bold' : 'solar:heart-linear'" :class="productStore.product.isSaved ? 'text-red-600' : ''" />
          </TsButton>
        </TsMediaEnd>
      </TsMedia>
      <div class="lg:py-6">
        <TsCarouselV2
          viewport-class="main"
          ref="mainCarouselRef"
          :navigation="false"
          :pagination="false"
          append-class="px-0"
        >
          <TsCarouselSlide
            v-for="(item, index) in props.data"
            :key="item"
            @click="()=>{product_images_and_videos_modal = true; selectedImageUrl = item?.src} "
            append-class="flex items-center justify-center lg:p-0"
          >
            <TsImage
              :data-testid="`${props.dataTestId}-image`"
              v-if="item.type === 'image'"
              :src="item.src"
              class="w-auto max-w-[200px] md:max-w-[400px] cursor-pointer"
              :width="isMobile ? '200' : '400'"
              :height="isMobile ? '200' : '400'"
              :alt="getAltFromProductImage(item.thumb)"
              loading="lazy"
            />
            <div
              v-if="item.type === 'video' && !runtimeConfig.public.featureFlags.enableVideoly"
              class="relative w-full max-lg:pt-10 lg:px-4"
            >
              <iframe
                :data-testid="`${props.dataTestId}-video`"
                id="yt-video-1"
                :src="item.src"
                class="w-full aspect-video"
                :title="props.product.full_name || props.product.name"
                allowfullscreen
              >
              </iframe>
            </div>
            <div
              v-if="item.type === 'video' && runtimeConfig.public.featureFlags.enableVideoly"
              class="relative w-full max-lg:pt-10 lg:px-4"
            >
            <div id="videoly-videobox-placeholder" data-type="large"></div>
            </div>
          </TsCarouselSlide>
        </TsCarouselV2>
      </div>
      <TsMedia append-class="lg:p-4">
        <TsMediaEnd>
          <div
            v-if="productStore.product && productStore.product.prices && productStore.product.prices.formatted && productStore.product.prices.formatted.discount_value && productStore.product.prices.formatted.discount_value.replace('€', '').replace(',', '.') > 0 
            || productStore.product.prices.formatted.discount_percentage !== '0%'" 
            class="absolute right-4 bottom-4"
          >
            <TsBadgeDiscountPercentage
              :data-testid="`pdp-${productStore.product.name}`"
              :discount="
                productStore.product && productStore.product.prices && productStore.product.prices.formatted &&
                parseFloat(productStore.product.prices.formatted.gross.replace('€', '').replace(',', '.')) > 100
                  ? productStore.product.prices.formatted.discount_value
                  : productStore.product.prices.formatted.discount_percentage
              "
            />
          </div>
        </TsMediaEnd>
      </TsMedia>
    </div>
    <client-only>
  <Teleport to="body"> 
      <TsModal
          v-model:visible="product_images_and_videos_modal"
          append-class="rounded-xl relative overflow-auto w-11/12 max-h-[90%] lg:w-10/12 max-w-full"
          :data-testid="`${props.dataTestId}-modal`"
      >
        <template #modal-content>
          <TsCard
              append-class="p-0 bg-white relative"
              header-class="sticky top-0 flex items-center justify-between py-0 bg-natural-soft-white px-4"
              body-class="p-4"
          >
            <template #header>
              <div>
                <TsButton
                    v-for="tab in modal_tabs"
                    variant="secodary"
                    :append-class="
                    twMerge(
                      'border-0 rounded-none p-3',
                      active_tab === tab && 'border-b-2 border-primary'
                    )
                  "
                    @click="active_tab = tab"
                    :key="tab"
                    :label="tab[0].toUpperCase() + tab.slice(1)"
                />
              </div>
              <TsButton
                  icon="ic:round-close"
                  variant="secondary"
                  append-class="border-none p-0"
                  :data-testid="`close-modal-${props.dataTestId}`"
                  icon-class="size-6"
                  @click="product_images_and_videos_modal = false"
              />
            </template>
            <template #content>
              <div v-if="active_tab === 'images'">
                <TsProductImageGallery
                    :brand="props.product.brand"
                    :title="props.product.full_name || props.product.name"
                    :images="product_images"
                    :SelectedImage ="props.data[selected_index].src"
                />
              </div>
              <div v-if="active_tab === 'videos'">
                //   <TsProductVideoGallery :videos="product_videos" />
              </div>
            </template>
          </TsCard>
        </template>
      </TsModal>

  </Teleport>
</client-only>
  </div>
</template>
